import React, { useState, useEffect } from "react";
import { metadata } from "../Constants/metadata";
import PropTypes from "prop-types";
import { getCurrentUser } from "../Services/usersApi";
import { useTranslation } from "react-i18next";

export default function SocialMedia({ setSocialMedia }) {
  const [val, setVal] = useState([]);
  const [alert, setAlert] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    getCurrentUser().then((response) => {
      const metadataRaw = response?.data?.metadata;
      let socialMedia = metadataRaw?.socialMedia || [];
      
      if (!Array.isArray(socialMedia)) {
        socialMedia = Object.entries(socialMedia).map(([key, value]) => ({ [key]: value }));
      }
      
      const flattenedSocialMedia = socialMedia.flatMap(item => 
        Object.entries(item).map(([key, value]) => ({ [key]: value }))
      );
      
      setVal(flattenedSocialMedia);
    });
  }, []);

  useEffect(() => {
    if (alert) {
      setTimeout(() => setAlert(false), 10000);
    }
  }, [alert]);

  const handleAdd = () => {
    if (val.length >= metadata.length) return;

    if (val.length > 0) {
      const lastEntry = val[val.length - 1];
      const [lastKey] = Object.keys(lastEntry);
      const lastValue = lastEntry[lastKey];

      if (!lastKey || !lastValue.trim()) {
        setAlert(t("pleaseFillPreviousInput"));
        return;
      }
    }

    setVal([...val, {}]);
  };

  const handleDelete = (index) => {
    const updatedVal = [...val].filter((_, idx) => idx !== index);
    setVal(updatedVal);
    setSocialMedia(updatedVal);
  };

  const handleSocialMediaSelectChanges = (event, idx) => {
    const selectedPlatform = event.target.value;
    if (!selectedPlatform) return;

    const temp = [...val];
    const isDuplicate = temp.some((item, i) => 
      i !== idx && Object.keys(item)[0] === selectedPlatform
    );

    if (isDuplicate) {
      setAlert(t("canNotSelectSameSocialMediaTwice"));
      return;
    }

    if (Object.keys(temp[idx]).length === 0) {
      temp[idx] = { [selectedPlatform]: "" };
    } else {
      const currentKey = Object.keys(temp[idx])[0];
      temp[idx] = { [selectedPlatform]: temp[idx][currentKey] || "" };
    }

    setVal(temp);
    setSocialMedia(temp);
  };

  const handleSocialMediaLinkChanges = (event, idx) => {
    const temp = [...val];
    const currentPlatform = Object.keys(temp[idx])[0];
    temp[idx][currentPlatform] = event.target.value;
    setVal(temp);
  };

  const handleLinkValidation = (event, idx) => {
    const temp = [...val];
    const currentPlatform = Object.keys(temp[idx])[0];
    const value = event.target.value.trim();
    
    const r = /^(https?:\/\/)?([\w-]+\.)+[\w-]+([\w- ./?%&=]*)?$/;
    
    if (value === "" || r.test(value)) {
      temp[idx][currentPlatform] = value;
      setVal(temp);
      setSocialMedia(temp);
      setAlert(false);
    } else {
      setAlert(t("pleaseEnterAValidLink"));
    }
  };

  return (
    <div>
      <div className="container w-auto px-5 py-2 bg-gray-900">
        <div className="bg-white mt-4 p-6 space-y-10">
          <h2
            className="text-slate-800 text-lg mb-4 font-medium title-font"
            style={{
              fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
            }}
          >
            {t("metadata")}
            <div className="my-4 bg-gray-600 h-[1px]"></div>
          </h2>

          <div className="relative mb-4">
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-600"
              style={{
                fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif",
              }}
            >
              {t("add_social_media")}
            </label>
            <div className="relative mb-4">
              <div className="relative mb-4 mt-2 border-white">
                {val && val.length > 0 && val.map((data, i) => (
                  <div key={i} className="items-stretch py-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="col-span-6 sm:col-span-1 mt-1 px-0 mr-2">
                      <label className="block text-md font-medium text-gray-600">
                        {t("select")}
                      </label>
                      <select
                        className="border p-3.5 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                        value={Object.keys(data)[0] || ""}
                        onChange={(e) => handleSocialMediaSelectChanges(e, i)}
                      >
                        <option value="" disabled>
                          {t("selectOption")}
                        </option>
                        {metadata
                          .filter(option => {
                            if (Object.keys(data).length === 0) return true;
                            return !val.some(item => 
                              Object.keys(item)[0] === option && item !== data
                            );
                          })
                          .map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="mt-1 px-0 ml-2">
                      <label className="block text-md font-medium text-gray-600">
                        {t("link")}
                      </label>
                      <input
                        type="text"
                        onBlur={(e) => handleLinkValidation(e, i)}
                        value={val[i][Object.keys(val[i])[0]] || ""}
                        onChange={(e) => handleSocialMediaLinkChanges(e, i)}
                        className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
                        placeholder="https://www.example.com/"
                      />
                    </div>
                    <div className="flex items-center ml-2 hidden md:flex">
                      <button onClick={() => handleDelete(i)}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-5 h-5"
                          viewBox="0 0 512 512"
                        >
                          <path d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {alert && (
                  <div className="mt-4 mb-10 md:mb-4 mt-2 text-sm text-red-600">
                    {alert}
                  </div>
                )}

                <button
                  className={`bg-gradient-to-r mt-4 font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full transition duration-300 ease-in-out ${
                    val.length >= metadata.length ? 'opacity-50 cursor-not-allowed' : `
                    hover:from-gray-700 hover:to-slate-600
                    hover:scale-[1.01] sm:hover:scale-[1.015] md:hover:scale-[1.02] lg:hover:scale-[1.025]
                  `
                  }`}
                  onClick={handleAdd}
                  disabled={val.length >= metadata.length}
                >
                  {t("add_social_media")}
                </button>

                
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SocialMedia.propTypes = {
  setSocialMedia: PropTypes.func.isRequired,
};